import React, { useEffect, useRef, useState } from 'react';
import { Input } from 'baseui/input';
import { Textarea } from 'baseui/textarea';
import { FormControl } from 'baseui/form-control';
import { RotateCcw } from 'lucide-react';
import { translate } from '../utils/translate';

const InputComponent = Input as any;
const TextareaComponent = Textarea as any;
const FormControlComponent = FormControl as any;

type Lang = 'en' | 'es';

interface BilingualFieldProps {
  label: string;
  caption?: string;
  language: Lang;
  valueEn: string;
  valueEs: string;
  /** Called with the language whose value just changed (either via typing or auto-translate). */
  onChange: (lang: Lang, value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  minHeight?: string;
}

/**
 * Single-input field that backs two language values (EN + ES). Renders only the
 * input for `language`; when the user types, after a short debounce the OTHER
 * language is filled by calling the public translate helper.
 *
 * Auto-translation is only a starting suggestion, not a locked value: once the
 * user manually edits a language it is marked "dirty" (touched) and auto-translate
 * will no longer overwrite that language. A small regenerate control lets the
 * user explicitly re-run the suggestion for the currently visible language,
 * clearing its dirty flag.
 */
export const BilingualField: React.FC<BilingualFieldProps> = ({
  label, caption, language, valueEn, valueEs, onChange, placeholder, multiline, minHeight,
}) => {
  const visibleValue = language === 'en' ? valueEn : valueEs;
  const otherValue = language === 'en' ? valueEs : valueEn;
  const otherLang: Lang = language === 'en' ? 'es' : 'en';
  const debounceRef = useRef<number | null>(null);
  // Per-language baseline of the last text we auto-translated FROM, so switching
  // tabs (or re-rendering) doesn't spuriously re-trigger a translation.
  const lastTranslatedFrom = useRef<{ en: string; es: string }>({ en: valueEn.trim(), es: valueEs.trim() });
  // Which languages the user has edited by hand — once dirty, auto-translate must
  // not overwrite that language until the user explicitly regenerates it.
  // A language that already has non-empty content when this field first mounts
  // (an existing record loaded from the backend, or a pre-filled template value)
  // counts as "edited by hand" too: auto-translate should only ever fill in a
  // field that's genuinely empty, never clobber content that's already there.
  const [dirty, setDirty] = useState<{ en: boolean; es: boolean }>(() => ({
    en: valueEn.trim().length > 0,
    es: valueEs.trim().length > 0,
  }));

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const trimmed = visibleValue.trim();
    if (!trimmed) return;
    if (trimmed === lastTranslatedFrom.current[language]) return;
    if (dirty[otherLang]) {
      // The other language was edited manually — don't clobber it, just update the
      // baseline so we don't keep re-checking the same text.
      lastTranslatedFrom.current[language] = trimmed;
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      const translated = await translate(trimmed, language, otherLang);
      lastTranslatedFrom.current[language] = trimmed;
      if (translated && translated !== otherValue) {
        onChange(otherLang, translated);
      }
    }, 700);
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
    // We deliberately omit valueEn/valueEs/onChange/otherValue from deps to avoid
    // a feedback loop when the auto-translation writes back to the form state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleValue, language, dirty, otherLang]);

  const handleChange = (value: string) => {
    setDirty(prev => (prev[language] ? prev : { ...prev, [language]: true }));
    onChange(language, value);
  };

  const handleRegenerate = async () => {
    const source = otherValue.trim();
    if (!source) return;
    const translated = await translate(source, otherLang, language);
    if (translated) {
      lastTranslatedFrom.current[otherLang] = source;
      setDirty(prev => ({ ...prev, [language]: false }));
      onChange(language, translated);
    }
  };

  return (
    <FormControlComponent
      label={
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {label}
          {dirty[language] && otherValue.trim() && (
            <button
              type="button"
              onClick={handleRegenerate}
              title="Regenerate auto-translation from the other language"
              aria-label="Regenerate auto-translation"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                color: '#6B7280',
              }}
            >
              <RotateCcw size={12} />
            </button>
          )}
        </span>
      }
      caption={caption}
    >
      {multiline ? (
        <TextareaComponent
          size="compact"
          value={visibleValue}
          placeholder={placeholder}
          onChange={(e: any) => handleChange(e.target.value)}
          overrides={{ Input: { style: { minHeight: minHeight || '80px', resize: 'vertical' } } }}
        />
      ) : (
        <InputComponent
          size="compact"
          value={visibleValue}
          placeholder={placeholder}
          onChange={(e: any) => handleChange(e.target.value)}
        />
      )}
    </FormControlComponent>
  );
};
